import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  errorText, useEmployeeFieldMap, useSaveEmployeeFieldMap,
} from '@/api/hrms-sync';
import { HrmsFieldPicker } from '@/components/HrmsFieldPicker';
import { fmtWhen } from './format';

/**
 * Which uKnowva profile field feeds each employee field.
 *
 * Employees are only created or updated once this is confirmed: a guessed
 * "date of joining" that is really the confirmation date changes proration
 * for everyone. Until then the employee records are still stored — every
 * profile field of every user — just not turned into payroll employees.
 */
export function EmployeeFieldMapPanel({ canEdit }: { canEdit: boolean }) {
  const { data, isLoading, error, refetch } = useEmployeeFieldMap(true);
  const save = useSaveEmployeeFieldMap();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    for (const r of data.fields) if (r.hrms_field) next[r.payroll_field] = r.hrms_field;
    setDraft(next);
  }, [data]);

  const takenBy = useMemo(() => {
    const labels = Object.fromEntries((data?.fields ?? []).map((r) => [r.payroll_field, r.label]));
    const out: Record<string, string> = {};
    for (const [payrollField, code] of Object.entries(draft)) {
      if (code) out[code] = labels[payrollField] ?? payrollField;
    }
    return out;
  }, [data, draft]);

  if (isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error">{errorText(error)}</Alert>;
  if (!data) return null;

  const counts: Record<string, number> = {};
  Object.values(draft).forEach((v) => { if (v) counts[v] = (counts[v] ?? 0) + 1; });
  const missing = data.fields.filter((r) => r.required && !draft[r.payroll_field]);
  const dup = Object.entries(counts).filter(([, n]) => n > 1).map(([k]) => k);

  const submit = async () => {
    setDone(false);
    try {
      await save.mutateAsync(
        Object.fromEntries(data.fields.map((r) => [r.payroll_field, draft[r.payroll_field] || null])),
      );
      setDone(true);
      void refetch();
    } catch { /* shown below */ }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        {data.confirmed ? (
          <Chip color="success" size="small"
            label={`Confirmed${data.confirmed_meta?.at ? ` ${fmtWhen(data.confirmed_meta.at)}` : ''}${data.confirmed_meta?.by ? ` by ${data.confirmed_meta.by}` : ''}`} />
        ) : (
          <Chip color="warning" size="small" label="Not confirmed — employees are stored, not imported" />
        )}
      </Stack>
      <Table size="small" sx={{ '& td, & th': { py: 0.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '32%' }}>Employee field</TableCell>
            <TableCell>uKnowva profile field</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.fields.map((r) => {
            const v = draft[r.payroll_field] ?? '';
            return (
              <TableRow key={r.payroll_field} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={r.required ? 700 : 500}>
                    {r.label}{r.required && <Box component="span" color="error.main"> *</Box>}
                  </Typography>
                  {r.note && <Typography variant="caption" color="text.secondary">{r.note}</Typography>}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HrmsFieldPicker
                      value={v}
                      options={data.available}
                      required={r.required}
                      disabled={!canEdit}
                      takenBy={takenBy}
                      error={(r.required && !v) || (Boolean(v) && (counts[v] ?? 0) > 1)}
                      onChange={(code) => setDraft({ ...draft, [r.payroll_field]: code })}
                    />
                    {r.status === 'auto' && r.hrms_field === v && (
                      <Chip size="small" variant="outlined"
                        color={r.confidence === 'high' ? 'success' : 'warning'}
                        label={`auto · ${r.confidence ?? '?'}`} />
                    )}
                  </Stack>
                  {r.missing_on_instance && (
                    <Typography variant="caption" color="error">
                      This field no longer exists on the instance
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {(missing.length > 0 || dup.length > 0) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {missing.length > 0 && <>Required: {missing.map((m) => m.label).join(', ')}. </>}
          {dup.length > 0 && <>Mapped twice: {dup.join(', ')}.</>}
        </Alert>
      )}
      {save.error && <Alert severity="error" sx={{ mt: 2 }}>{errorText(save.error)}</Alert>}
      {done && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Employee mapping confirmed. The next sync of “Employees” creates and updates them
          (identity and org placement only — never salary or status).
        </Alert>
      )}
      {canEdit && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" onClick={submit}
            disabled={save.isPending || missing.length > 0 || dup.length > 0}>
            {save.isPending ? 'Saving…' : 'Save & confirm'}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
